/**
 * LOYALTY TYPOGRAPHY — the four text styles of the member portal.
 *
 * Headline, Body, Buttons and Small print each carry a font, a size step, bold,
 * italic, alignment and a colour, on top of one overall "Text size" scale. The
 * numbers come from the SHARED helpers (lib/designTokens) so a size step means
 * exactly what it means on the Patron Points pages — this file only reads them.
 *
 * NOTHING SAVED = NOTHING CHANGED. Every value defaults to "unset", every
 * helper then returns undefined and `portalCss` returns an empty string, so a
 * clinic that never opened these controls renders byte for byte as before.
 */
import { SCALE_DEFAULT, SCALE_MAX, SCALE_MIN, bodyFontSize, bodyPx, fontStack,
    headlineFontSize, headlinePx } from '../../lib/designTokens';

export const TEXT_STYLES = ['headline', 'body', 'button', 'small'];
export const ALIGNS = ['left', 'center', 'right'];

/** The data attribute the scoped rules hang on. */
export const TYPE_SCOPE = 'loy-type';

const scaleOf = (theme) => {
    const n = Number(theme?.text_scale);
    if (!Number.isFinite(n)) return SCALE_DEFAULT;
    return Math.max(SCALE_MIN, Math.min(SCALE_MAX, n));
};

const factor = (theme) => scaleOf(theme) / 100;

const align = (value) => (ALIGNS.includes(value) ? value : undefined);

/** Font, weight, slant and colour — the part every style shares. */
const face = (theme, key, fallbackFont) => ({
    fontFamily: fontStack(theme[`${key}_font`] || fallbackFont || '') || undefined,
    fontWeight: theme[`${key}_bold`] ? 800 : undefined,
    fontStyle: theme[`${key}_italic`] ? 'italic' : undefined,
    color: theme[`${key}_colour`] || undefined,
});

export const headlineStyle = (theme = {}) => ({
    ...face(theme, 'headline'),
    // EXPLICIT: a body set to italic (or bold) must not drag the headline with
    // it — each style says what it is.
    fontStyle: theme.headline_italic ? 'italic' : 'normal',
    fontSize: headlineFontSize(theme.headline_size, scaleOf(theme), 26)
        || headlinePx(theme.headline_size) || 26,
    textAlign: align(theme.headline_align),
});

/** The portal's own text. Sizes live in `portalCss`, which scales the classes. */
export const bodyStyle = (theme = {}) => ({
    ...face(theme, 'body'),
    textAlign: align(theme.body_align),
});

export const buttonStyle = (theme = {}) => {
    const { color, ...rest } = face(theme, 'button', theme.body_font);
    return {
        ...rest,                          // the button's own text colour wins
        fontStyle: theme.button_italic ? 'italic' : 'normal',
        fontWeight: theme.button_bold ? 800 : 600,
        fontSize: bodyFontSize(theme.button_size || theme.body_size, scaleOf(theme), 18)
            || bodyPx(theme.button_size || theme.body_size) || 18,
    };
};

/** Every face the portal must actually download for this theme. */
export const chosenFonts = (theme = {}) => [
    theme.headline_font, theme.body_font, theme.button_font, theme.small_font,
].filter(Boolean);

export const smallStyle = (theme = {}) => face(theme, 'small', theme.body_font);

const sizeFactor = (theme, key, basePx) => {
    const step = bodyPx(theme[`${key}_size`]);
    return ((step || basePx) / basePx) * factor(theme);
};

const px = (rem, mult) => `${Math.round(rem * 16 * mult * 10) / 10}px`;

/** Body classes scale with the Body step, the tiny ones with Small print. */
const BODY_CLASSES = [['.text-sm', 0.875], ['.text-base', 1], ['.text-lg', 1.125],
    ['.text-xl', 1.25], ['.text-2xl', 1.5]];
const SMALL_CLASSES = [['.text-xs', 0.75], ['.text-\\[11px\\]', 0.6875]];

/**
 * The ONE scoped stylesheet: it reaches every small line of the portal without
 * touching the markup, and it is only emitted when the clinic actually chose
 * something. Returns '' otherwise.
 */
export const portalCss = (theme = {}) => {
    const rules = [];
    const at = (sel) => `[data-${TYPE_SCOPE}="on"] ${sel}`;
    const bodyMult = sizeFactor(theme, 'body', 16);
    const smallMult = sizeFactor(theme, 'small', 12);
    if (Math.abs(bodyMult - 1) > 0.001) {
        BODY_CLASSES.forEach(([cls, rem]) =>
            rules.push(`${at(cls)}{font-size:${px(rem, bodyMult)}}`));
    }
    if (Math.abs(smallMult - 1) > 0.001) {
        SMALL_CLASSES.forEach(([cls, rem]) =>
            rules.push(`${at(cls)}{font-size:${px(rem, smallMult)}}`));
    }
    const small = smallStyle(theme);
    const bits = [
        small.fontFamily && `font-family:${small.fontFamily}`,
        theme.small_bold && 'font-weight:800',
        theme.small_italic && 'font-style:italic',
        theme.small_colour && `color:${theme.small_colour}`,
    ].filter(Boolean);
    if (bits.length) {
        rules.push(`${at('.text-xs')},${at('.text-\\[11px\\]')}{${bits.join(';')}}`);
    }
    return rules.join('');
};

/** Everything back to the look the portal has with nothing saved. */
export const TYPOGRAPHY_DEFAULTS = {
    text_scale: 100,
    headline_font: '', headline_size: 'md', headline_bold: false,
    headline_italic: false, headline_align: '', headline_colour: '',
    body_font: '', body_size: 'md', body_bold: false, body_italic: false,
    body_align: '', body_colour: '',
    button_font: '', button_size: '', button_bold: false, button_italic: false,
    small_font: '', small_size: '', small_bold: false, small_italic: false,
    small_colour: '',
};
